import { useFeatureFlag } from '@src/hooks';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import { useAuxiliaryOrderData } from '@src/providers/AuxiliaryOrderDataProvider';
import {
  DEFAULT_MAX_ROWS,
  ExecTypeEnum,
  ORDER,
  useConstant,
  useDynamicCallback,
  useSubscription,
  useWsPaginationLimiter,
  wsStitchWith,
  type LimitReachedChangeEvent,
  type Order,
  type OrdStatusEnum,
  type UnifiedLiquidityEnum,
  type WebsocketRequest,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { ORDER_SUB_THROTTLE } from './tokens';

// TODO merge with def in BlotterTableOrderFilter
interface FilteredOrdersBlotterServerFilter {
  Statuses?: OrdStatusEnum[];
  Users?: string[];
  SubAccounts?: string[];
  Markets?: string[];
  SelectedMarkets?: string[];
  TradedMarkets?: string[];
  Sides?: string[];
  Strategies?: string[];
  Symbols?: string[];
  Warnings?: string[];
  ProductTypes?: string[];
  UnifiedLiquidity?: UnifiedLiquidityEnum[];
  HideApiCalls?: boolean;
  OrderID?: string;
  RFQID?: string;
  ClOrdID?: string;
  ParentOrderID?: string;
  ParentRFQID?: string;
  StartDate?: string;
  EndDate?: string;
}

interface OrderRequest extends WebsocketRequest, FilteredOrdersBlotterServerFilter {
  // TODO
}

interface UseFilteredOrdersBlotterObsParams {
  serverFilter?: FilteredOrdersBlotterServerFilter;
}

export const useFilteredOrdersBlotterObs = ({ serverFilter }: UseFilteredOrdersBlotterObsParams) => {
  const [orderRequest, setOrderRequest] = useState<OrderRequest | null>(null);
  const { enableOrderAuxData } = useFeatureFlag();

  const blotterTag = useConstant(`FILTERED_ORDERS_${uuid()}`);

  useEffect(() => {
    setOrderRequest(currentRequest => {
      const maybeNewRequest: OrderRequest = {
        name: ORDER,
        tag: blotterTag,
        sort_by: '-SubmitTime',
        CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
        Throttle: ORDER_SUB_THROTTLE,
        ...serverFilter,
      };
      if (enableOrderAuxData) {
        maybeNewRequest.IncludeAuxiliaryData = true;
      }

      return isEqual(currentRequest, maybeNewRequest) ? currentRequest : maybeNewRequest;
    });
  }, [blotterTag, enableOrderAuxData, serverFilter]);

  const { data: orderObs, nextPage, error: errorObservable } = useSubscription<Order>(orderRequest, { loadAll: false });

  const { getConfig } = useOrgConfiguration();
  // Get a constant limit (user has to refresh for it to change)
  // Divide the default by two because the max is being applied to two streams as opposed to one
  const limit = useConstant(getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS));

  const [limitReachedState, setLimitReachedState] = useState<LimitReachedChangeEvent<Order> | undefined>(undefined);

  const orderPaginationLimiter = useWsPaginationLimiter<Order>({
    startingLimit: limit,
    nextPage: nextPage,
    onLimitReachedChange: setLimitReachedState,
  });

  const pagedOrdersObs = useMemo(
    () => orderObs.pipe(orderPaginationLimiter.wsPaginationLimiterInstance),
    [orderObs, orderPaginationLimiter.wsPaginationLimiterInstance]
  );

  const raisePaginationLimits = useDynamicCallback(() => {
    orderPaginationLimiter.raiseLimit(limit);
  });

  const { hedgeOrderStatusObs, orderAnnotationsObs } = useAuxiliaryOrderData();

  const stitchedDataObs = useMemo(
    () =>
      pagedOrdersObs.pipe(
        wsStitchWith({
          secondarySource: hedgeOrderStatusObs,
          getPrimaryTypeKey: order => order.OrderID,
          getSecondaryTypeKey: hos => hos.InitiatingOrderID,
          stitch: (order, hos) => {
            if (hos) {
              order.enrichWithHedgeOrderStatus(hos);
            }
            return order;
          },
        }),
        wsStitchWith({
          secondarySource: orderAnnotationsObs,
          getPrimaryTypeKey: order => order.OrderID,
          getSecondaryTypeKey: annotation => annotation.OrderID,
          stitch: (order, annotation) => {
            if (annotation) {
              order.enrichWithOrderAnnotation(annotation);
            }
            return order;
          },
        })
      ),
    [pagedOrdersObs, hedgeOrderStatusObs, orderAnnotationsObs]
  );

  return { dataObservable: stitchedDataObs, raisePaginationLimits, limitReachedState, errorObservable };
};
